/**
 * @file     reportError.ts
 * @purpose  Fire-and-forget crash report to /api/error-report. Called from
 *           error.tsx / global-error.tsx boundaries and the global window
 *           error listener. Never throws. Attaches breadcrumbs + device
 *           context so a non-reproducible prod crash is still diagnosable,
 *           and uses sendBeacon (survives the page tearing down mid-crash).
 */
import { getBreadcrumbs } from './breadcrumbs';
import { isCspEvalBlocked } from './cspEvalBlocked';
import { isElementFromPointClickError } from './elementFromPointClick';
import { ga4Only } from '@/services/analytics/gtagDelivery';
import { errorClassOf } from '@/services/analytics/gaEventParams';

export interface ErrorReport {
  message?: string;
  stack?: string;
  digest?: string;
  boundary?: string;
  /** React component stack when available (class boundary / global handler). */
  componentStack?: string;
  /** Extra diagnostic key/values (e.g. hydration locale fingerprint). */
  context?: Record<string, string>;
}

export function crashGaParams(error: ErrorReport, route: string): Record<string, string> {
  return {
    digest: error.digest ?? '(none)',
    boundary: error.boundary ?? 'unknown',
    route,
    error_name: errorClassOf(error),
  };
}

export function reportError(error: ErrorReport): void {
  if (typeof window === 'undefined') return;
  if (isCspEvalBlocked({ message: error.message })) return;
  if (isElementFromPointClickError({ message: error.message, stack: error.stack })) return;
  try {
    const payload = {
      ...error,
      url: window.location.href,
      route: window.location.pathname,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      online: navigator.onLine,
      referrer: document.referrer || undefined,
      breadcrumbs: getBreadcrumbs(),
    };
    const body = JSON.stringify(payload);

    // sendBeacon is delivered even if the document is unloading or the React
    // tree is mid-teardown — more reliable than fetch() inside a crashing
    // boundary. Fall back to keepalive fetch if beacon is unavailable/refused.
    const beaconOk =
      typeof navigator.sendBeacon === 'function' &&
      navigator.sendBeacon('/api/error-report', new Blob([body], { type: 'application/json' }));
    if (!beaconOk) {
      void fetch('/api/error-report', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {});
    }

    // GA4 crash beacon, fired directly via gtag (not the analytics service):
    // the service abstraction is part of the app that just crashed and can't
    // be trusted here. Best-effort, guarded. Admin crashes are agent work, kept
    // out of GA4 like every other admin event (liveAnalyticsService).
    if (!window.location.pathname.startsWith('/admin')) {
      const w = window as unknown as { gtag?: (...a: unknown[]) => void };
      w.gtag?.('event', 'app_crashed', ga4Only(crashGaParams(error, window.location.pathname)));
    }
  } catch {
    // cannot throw inside an error boundary
  }
}
